import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar } from "lucide-react";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";

const useTypewriter = (text: string, speed = 60, delay = 800) => {
  const [displayed, setDisplayed] = useState("");
  useEffect(() => {
    let i = 0;
    const timeout = setTimeout(() => {
      const interval = setInterval(() => {
        setDisplayed(text.slice(0, i + 1));
        i++;
        if (i >= text.length) clearInterval(interval);
      }, speed);
      return () => clearInterval(interval);
    }, delay);
    return () => clearTimeout(timeout);
  }, [text, speed, delay]);
  return displayed;
};

const Hero = () => {
  const tagline = useTypewriter("Beraterin · Business-Coachin · Dozentin", 50, 600);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(pointer: coarse)").matches) {
      setIsTouch(true);
      return;
    }
    const handler = (e: MouseEvent) => {
      setMouse({
        x: (e.clientX / window.innerWidth - 0.5) * 2,
        y: (e.clientY / window.innerHeight - 0.5) * 2,
      });
    };
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, []);

  const orbs = [
    { cls: "top-20 right-10 w-64 h-64 bg-purple-accent/10", dur: 8, del: 0, px: 25 },
    { cls: "bottom-20 left-10 w-48 h-48 bg-accent/10", dur: 6, del: 1, px: 20 },
    { cls: "top-1/2 right-1/3 w-40 h-40 bg-accent/8", dur: 7, del: 2, px: 30 },
    { cls: "top-10 left-1/4 w-56 h-56 bg-purple-accent/8", dur: 9, del: 3, px: 15 },
    { cls: "bottom-1/3 right-1/4 w-36 h-36 bg-accent/6", dur: 5, del: 1.5, px: 22 },
  ];

  return (
    <section id="hero" className="relative min-h-screen flex items-center bg-[var(--gradient-hero)] pt-20 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/95 via-primary/90 to-transparent" />
      <div className="absolute inset-0 bg-dot-pattern opacity-20" />

      {orbs.map((orb, i) => (
        <motion.div
          key={i}
          className={`absolute ${orb.cls} rounded-full blur-3xl`}
          animate={{ scale: [1, 1.25, 1], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: orb.dur, repeat: Infinity, ease: "easeInOut", delay: orb.del }}
          style={!isTouch ? {
            transform: `translate(${-mouse.x * orb.px}px, ${-mouse.y * orb.px}px)`,
          } : undefined}
        />
      ))}

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 md:py-24">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          <motion.div
            className="text-white space-y-10"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="space-y-6">
              <motion.h1
                className="text-6xl md:text-7xl lg:text-8xl font-extrabold leading-tight tracking-tight gradient-text-shimmer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Maria Keutel
              </motion.h1>
              <motion.p
                className="text-2xl md:text-3xl lg:text-4xl text-accent font-semibold h-[1.3em]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.3 }}
              >
                {tagline}
                <motion.span
                  className="inline-block w-[3px] h-[1em] bg-accent ml-1 align-middle"
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.6, repeat: Infinity, repeatType: "reverse" }}
                />
              </motion.p>
            </div>
            <motion.p
              className="text-xl md:text-2xl lg:text-3xl leading-relaxed font-light"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Beratung mit Klarheit, Struktur und Strategie
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row gap-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <Button variant="hero" size="lg" className="group text-lg px-10 py-7" asChild>
                <a href="https://outlook.office365.com/book/Grndungsberatung@walbee.de/?ismsaljsauthenabled=true" target="_blank" rel="noopener noreferrer">
                  <Calendar className="mr-2 h-6 w-6" />
                  Zum Termin
                  <ArrowRight className="ml-2 h-6 w-6 transition-transform group-hover:translate-x-2" />
                </a>
              </Button>
              <Button variant="outline" size="lg" className="text-lg px-10 py-7 border-2 border-white text-white hover:bg-white hover:text-primary font-semibold min-h-[44px]" asChild>
                <a href="#services">Leistungen</a>
              </Button>
            </motion.div>
          </motion.div>

          <motion.div
            className="relative lg:ml-auto"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
          >
            <div className="relative">
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-accent/30 to-purple-accent/30 rounded-2xl transform rotate-3"
                animate={{ rotate: [3, 5, 3] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              />
              {/* Aspect-Ratio-Wrapper reserviert Platz → verhindert CLS */}
              <motion.div
                className="relative w-full max-w-md mx-auto"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
                style={{ aspectRatio: "896 / 1120" }}
              >
                <picture>
                  <source
                    type="image/webp"
                    srcSet="/lovable-uploads/e533d809-72ca-44e2-9acd-64bac20b0579-640.webp 640w, /lovable-uploads/e533d809-72ca-44e2-9acd-64bac20b0579-1024.webp 1024w, /lovable-uploads/e533d809-72ca-44e2-9acd-64bac20b0579-1600.webp 1600w"
                    sizes="(max-width: 768px) 90vw, 448px"
                  />
                  <img
                    src="/lovable-uploads/e533d809-72ca-44e2-9acd-64bac20b0579.png"
                    alt="Maria Keutel - Unternehmensberaterin"
                    width={896}
                    height={1120}
                    // @ts-expect-error - camelCase Prop wird korrekt gerendert
                    fetchpriority="high"
                    decoding="async"
                    className="w-full h-full object-cover rounded-2xl shadow-[var(--shadow-large)]"
                  />
                </picture>
              </motion.div>
              <motion.div
                className="absolute -bottom-8 -right-8 w-32 h-32 bg-accent rounded-full opacity-20 blur-3xl"
                animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.3, 0.2] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                className="absolute -top-8 -left-8 w-24 h-24 bg-purple-accent rounded-full opacity-20 blur-2xl"
                animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.35, 0.2] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
          <motion.div
            className="w-1 h-3 bg-white rounded-full mt-2"
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
